import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarOff, Plus, Loader2, Clock, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const leaveTypes: Record<string, string> = {
  sick: "مرضي",
  personal: "شخصي",
  family: "عائلي",
  travel: "سفر",
  other: "أخرى",
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock }> = {
  pending: { label: "قيد المراجعة", variant: "secondary", icon: Clock },
  approved: { label: "موافق عليه", variant: "default", icon: CheckCircle2 },
  rejected: { label: "مرفوض", variant: "destructive", icon: XCircle },
};

interface Props {
  children: { id: string; full_name: string }[];
  nurseryId: string;
}

export default function ParentLeaveRequests({ children, nurseryId }: Props) {
  const { user } = useAuth();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [childId, setChildId] = useState(children[0]?.id || "");
  const [leaveType, setLeaveType] = useState("sick");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("leave_requests")
      .select("*, children(full_name)")
      .eq("parent_id", user.id)
      .order("created_at", { ascending: false });
    setRequests(data || []);
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!user || !childId || !startDate || !endDate) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error("تاريخ الانتهاء يجب أن يكون بعد تاريخ البداية");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("leave_requests").insert({
      nursery_id: nurseryId,
      child_id: childId,
      parent_id: user.id,
      leave_type: leaveType,
      start_date: startDate,
      end_date: endDate,
      reason: reason || null,
    });

    setSubmitting(false);
    if (error) {
      toast.error("خطأ في إرسال الطلب: " + error.message);
    } else {
      toast.success("تم إرسال طلب الإجازة بنجاح");
      setOpen(false);
      setStartDate("");
      setEndDate("");
      setReason("");
      fetchRequests();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("leave_requests").delete().eq("id", id);
    if (error) {
      toast.error("خطأ في حذف الطلب");
    } else {
      toast.success("تم حذف الطلب");
      setRequests((prev) => prev.filter((r) => r.id !== id));
    }
  };

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarOff className="w-5 h-5 text-primary" />
            طلبات الإجازة
          </CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gradient-hero border-0">
                <Plus className="w-4 h-4 ml-1" />
                طلب إجازة
              </Button>
            </DialogTrigger>
            <DialogContent dir="rtl" className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>تقديم طلب إجازة</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>الطفل</Label>
                  <Select value={childId} onValueChange={setChildId}>
                    <SelectTrigger><SelectValue placeholder="اختر الطفل" /></SelectTrigger>
                    <SelectContent>
                      {children.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>نوع الإجازة</Label>
                  <Select value={leaveType} onValueChange={setLeaveType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(leaveTypes).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>من تاريخ</Label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label>إلى تاريخ</Label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} dir="ltr" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>السبب (اختياري)</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="اكتب سبب الإجازة..." rows={3} />
                </div>
                <Button onClick={handleSubmit} disabled={submitting} className="w-full gradient-hero border-0">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
                  إرسال الطلب
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center">
            <CalendarOff className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">لا توجد طلبات إجازة</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="divide-y">
              {requests.map((req) => {
                const sc = statusConfig[req.status] || statusConfig.pending;
                const StatusIcon = sc.icon;
                return (
                  <div key={req.id} className="p-4 flex items-start gap-3 hover:bg-muted/30 transition-colors">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      req.status === "approved" ? "bg-primary/10" : req.status === "rejected" ? "bg-destructive/10" : "bg-muted"
                    }`}>
                      <StatusIcon className={`w-4 h-4 ${
                        req.status === "approved" ? "text-primary" : req.status === "rejected" ? "text-destructive" : "text-muted-foreground"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-foreground text-sm">{req.children?.full_name}</p>
                        <Badge variant="outline" className="text-[10px]">{leaveTypes[req.leave_type] || req.leave_type}</Badge>
                        <Badge variant={sc.variant} className="text-[10px]">{sc.label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(req.start_date), "dd MMM", { locale: ar })} → {format(new Date(req.end_date), "dd MMM yyyy", { locale: ar })}
                      </p>
                      {req.reason && <p className="text-xs text-muted-foreground mt-1">{req.reason}</p>}
                      {req.admin_notes && (
                        <p className="text-xs text-primary mt-1 bg-primary/5 rounded px-2 py-1">
                          ملاحظات الإدارة: {req.admin_notes}
                        </p>
                      )}
                    </div>
                    {req.status === "pending" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => handleDelete(req.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
